import { Fragment, useCallback, useEffect, useState } from "react";

import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";

import { logout } from "@/common/services/auth/Auth.service";
import { useFarmModules } from "@/shared/farm-modules";

import { useWidth } from "../../../../shared/hooks/useWidth";
import { LogoutIcon } from "../../../../shared/icons/navbar/LogoutIcon";
import {
  inCountry,
  inDropdown,
  inEnabledModule,
} from "../../services/menuItemFilters";
import { MenuItemType } from "../../services/menuItems";
import NavbarService from "../../services/NavbarService";
import MainMenuDropdown from "../MainMenuDropdown/MainMenuDropdown";

import { useMainMenuStyles } from "./MainMenu.styles";
import { MainMenuItem } from "./MainMenuItem";

interface Props {
  countryCode?: string;
  farmId: string;
  mobile?: boolean;
}

const MainMenu = ({ countryCode, farmId, mobile = false }: Props) => {
  const { pathname } = useLocation();
  const classes = useMainMenuStyles();
  const width = useWidth();
  const { isEnabled } = useFarmModules();

  const menuItems = NavbarService.getVisibleMainMenuItems([
    inCountry(countryCode),
    inEnabledModule(isEnabled),
  ]);
  const dropdownMenuItems = menuItems.filter(inDropdown);

  const isActive = useCallback(
    (menuItem: MenuItemType) => pathname.includes(menuItem.route),
    [pathname],
  );

  const getSelectedDropdownItem = useCallback(
    () => dropdownMenuItems.find((menuItem) => isActive(menuItem)),
    [dropdownMenuItems, isActive],
  );

  const [dropdownSelected, setDropdownSelected] = useState(
    getSelectedDropdownItem()?.id || dropdownMenuItems[0]?.id || "",
  );

  useEffect(() => {
    const selectedDropdownItem = getSelectedDropdownItem();
    if (selectedDropdownItem && selectedDropdownItem.id !== dropdownSelected) {
      setDropdownSelected(selectedDropdownItem.id);
    }
  }, [getSelectedDropdownItem, dropdownSelected]);

  return (
    <div className={mobile ? classes.mainMenuMobile : classes.mainMenuDesktop}>
      {menuItems
        .filter(
          (menuItem) =>
            !(
              (width === "md" || width === "lg") &&
              menuItem.dropdown &&
              menuItem.id !== dropdownSelected
            ),
        )
        .map((menuItem) => (
          <MainMenuItem
            farmId={farmId}
            key={menuItem.id}
            menuItem={menuItem}
            mobile={mobile}
          />
        ))}
      {(width === "md" || width === "lg") && (
        <MainMenuDropdown
          farmId={farmId}
          items={dropdownMenuItems}
          lastSelected={dropdownSelected}
        />
      )}
      {mobile && (
        <Fragment>
          <hr className={classes.separator} />
          <div
            className={`${classes.menuItemLink} ${classes.menuItemLinkMobile}`}
            id="main-menu-logout"
            onClick={logout}
            role="button"
            tabIndex={0}
          >
            <span className={classes.navlinkContentMobile}>
              <LogoutIcon className={classes.navlinkIcon} />
              <FormattedMessage id="UserMenu.logout" />
            </span>
          </div>
        </Fragment>
      )}
    </div>
  );
};

export { MainMenu };
